import { boxCenter } from './Detector';
import type { HeatmapRenderer } from './HeatmapRenderer';
import { applyHomography, type Homography3x3 } from './Homography';
import type { TrackedObject } from './Tracker';
import type { Trajectory } from './TrajectoryStore';
import { colorForTrack } from './TrackingRenderer';

export interface CourtViewOptions {
  widthMeters: number;
  heightMeters: number;
  padding?: number;
}

export class CourtView {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly opts: Required<CourtViewOptions>;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    opts: CourtViewOptions,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('court canvas 2d context unavailable');
    this.ctx = ctx;
    this.opts = { padding: 6, ...opts };
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  draw(
    tracks: readonly TrackedObject[],
    trajectories: readonly Trajectory[],
    H: Homography3x3,
    teamColors?: ReadonlyMap<number, string>,
    heatmap?: HeatmapRenderer,
  ): void {
    const { width, height } = this.canvas;
    const { widthMeters, heightMeters, padding } = this.opts;
    const innerW = width - padding * 2;
    const innerH = height - padding * 2;
    const sx = innerW / widthMeters;
    const sy = innerH / heightMeters;
    const scale = Math.min(sx, sy);
    const offsetX = padding + (innerW - widthMeters * scale) / 2;
    const offsetY = padding + (innerH - heightMeters * scale) / 2;

    const ctx = this.ctx;
    ctx.clearRect(0, 0, width, height);

    ctx.save();
    ctx.fillStyle = 'rgba(30, 40, 56, 0.85)';
    ctx.fillRect(offsetX, offsetY, widthMeters * scale, heightMeters * scale);
    if (heatmap) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(offsetX, offsetY, widthMeters * scale, heightMeters * scale);
      ctx.clip();
      ctx.translate(offsetX, offsetY);
      heatmap.draw(ctx, widthMeters * scale, heightMeters * scale);
      ctx.restore();
    }
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 1;
    ctx.strokeRect(offsetX, offsetY, widthMeters * scale, heightMeters * scale);
    ctx.beginPath();
    ctx.moveTo(offsetX + (widthMeters / 2) * scale, offsetY);
    ctx.lineTo(offsetX + (widthMeters / 2) * scale, offsetY + heightMeters * scale);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(
      offsetX + (widthMeters / 2) * scale,
      offsetY + (heightMeters / 2) * scale,
      1.8 * scale,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
    ctx.restore();

    const projectXY = (x: number, y: number): { px: number; py: number } | null => {
      const out = applyHomography({ x, y }, H);
      if (
        !Number.isFinite(out.x) ||
        !Number.isFinite(out.y) ||
        out.x < -widthMeters ||
        out.x > widthMeters * 2 ||
        out.y < -heightMeters ||
        out.y > heightMeters * 2
      ) {
        return null;
      }
      return { px: offsetX + out.x * scale, py: offsetY + out.y * scale };
    };

    ctx.save();
    for (const traj of trajectories) {
      if (traj.class === 'ball') continue;
      if (traj.points.length < 2) continue;
      const color = colorForTrack(traj.id, traj.class, teamColors);
      ctx.strokeStyle = withAlpha(color, 0.5);
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      let started = false;
      for (const p of traj.points) {
        const proj = projectXY(p.x, p.y);
        if (!proj) {
          started = false;
          continue;
        }
        if (!started) {
          ctx.moveTo(proj.px, proj.py);
          started = true;
        } else {
          ctx.lineTo(proj.px, proj.py);
        }
      }
      ctx.stroke();
    }
    ctx.restore();

    for (const t of tracks) {
      const c = boxCenter(t.box);
      const foot = { x: c.x, y: t.box.y + t.box.h };
      const proj = projectXY(foot.x, foot.y);
      if (!proj) continue;
      const color = colorForTrack(t.id, t.class, teamColors);
      ctx.fillStyle = color;
      ctx.globalAlpha = t.isGhost ? 0.4 : 1;
      ctx.beginPath();
      ctx.arc(proj.px, proj.py, t.class === 'ball' ? 4 : 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}
